import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Modal, Button } from '@components/ui';
import CompanySelector from '@components/shared/CompanySelector';
import { kazikCaplari, kazikDurumlari, testDurumlari } from '@mocks/materials/foreKazik';
import { mockCompanies } from '@mocks/firmalar';

/**
 * Props:
 *   onSubmit(data, isEdit) — Normal malzeme sayfasından kullanım
 *   onMaterialSubmit(enrichedData) — Finance modundan açıldığında
 *   submitLabel — Buton yazısı
 */
export default function ForeKazikModal({ isOpen, onClose, onSubmit, onMaterialSubmit, submitLabel, initialData = null, isLoading = false }) {
    const isEdit = !!initialData;
    const [selectedCompany, setSelectedCompany] = useState(
        initialData?.supplierId ? { id: initialData.supplierId, name: initialData.tedarikci } : null
    );

    const { register, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: initialData || { cap: 80, derinlikBirim: 'm', durum: 'planlandi', testDurumu: 'bekliyor' },
    });

    const handleFormSubmit = (data) => {
        data.tedarikci = selectedCompany?.name || null;
        data.supplierId = selectedCompany?.id || null;

        const enriched = {
            ...data,
            selectedCompany,
            calculatedTotal: data.maliyetToplam || 0,
            autoDescription: `Fore Kazık — ${data.kazikNo} (Ø${data.cap}cm)`,
        };

        if (onMaterialSubmit) {
            onMaterialSubmit(enriched);
        } else {
            onSubmit(enriched, isEdit);
        }
    };

    const inputClass = "w-full px-3 py-2.5 bg-bg-primary border border-border-light rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50";

    return (
        <Modal isOpen={isOpen} onClose={() => { reset(); onClose(); }} title={isEdit ? 'Kazık Düzenle' : 'Yeni Kazık'} size="lg"
            footer={<><Button variant="ghost" onClick={() => { reset(); onClose(); }} disabled={isLoading}>İptal</Button><Button variant="primary" onClick={handleSubmit(handleFormSubmit)} isLoading={isLoading}>{submitLabel || (isEdit ? 'Güncelle' : 'Kaydet')}</Button></>}>
            <form className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-text-primary mb-1.5">Kazık No <span className="text-error">*</span></label>
                        <input type="text" {...register('kazikNo', { required: true })} placeholder="FK-001" className={`${inputClass} font-mono ${errors.kazikNo ? 'border-error' : ''}`} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-primary mb-1.5">Blok <span className="text-error">*</span></label>
                        <input type="text" {...register('blok', { required: true })} placeholder="A Blok" className={inputClass} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-primary mb-1.5">Aksis/Konum</label>
                        <input type="text" {...register('konum')} placeholder="A1-B2" className={inputClass} />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-text-primary mb-1.5">Çap (cm) <span className="text-error">*</span></label>
                        <select {...register('cap', { required: true, valueAsNumber: true })} className={inputClass}>
                            {kazikCaplari.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-primary mb-1.5">Derinlik (m) <span className="text-error">*</span></label>
                        <input type="number" step="0.1" {...register('derinlik', { required: true, valueAsNumber: true })} placeholder="25" className={inputClass} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-primary mb-1.5">Beton (m³)</label>
                        <input type="number" step="0.1" {...register('betonMiktari', { valueAsNumber: true })} placeholder="15" className={inputClass} />
                    </div>
                </div>
                <div>
                    <CompanySelector
                        value={selectedCompany}
                        onChange={setSelectedCompany}
                        companies={mockCompanies}
                        companyType="subcontractor"
                        label="Müteahhit / Firma"
                    />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-text-primary mb-1.5">İmalat Durumu</label>
                        <select {...register('durum')} className={inputClass}>
                            {kazikDurumlari.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-primary mb-1.5">Test Durumu</label>
                        <select {...register('testDurumu')} className={inputClass}>
                            {testDurumlari.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                        </select>
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-text-primary mb-1.5">Not</label>
                    <textarea {...register('not')} rows={2} placeholder="Kazık notu..." className={`${inputClass} resize-none`} />
                </div>
            </form>
        </Modal>
    );
}
